// ─────────────────────────────────────────────────────────────────────────────
// Manager — Submit Expense Report
// Shows completed trip expenses for the team, lets manager add comments +
// mark reviewed per expense, then submits an HTML email to Finance leadership
// Works for both Ebek and Ebekmed managers.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";

const MONTHS_FULL = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];
const currentYear  = new Date().getFullYear();
const currentMonth = new Date().getMonth() + 1;
const YEARS        = Array.from({ length: 3 }, (_, i) => currentYear - i);

function fmtInr(n) {
  return parseFloat(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

// ── Print CSS injected once ──────────────────────────────────────────────────
const PRINT_CSS = `
  @media print {
    body * { visibility: hidden !important; }
    #mer-printable, #mer-printable * { visibility: visible !important; }
    #mer-printable { position: absolute; left: 0; top: 0; width: 100%; }
    .mer-no-print { display: none !important; }
  }
`;

// ── Group raw rows → {employeeName → {tripId → {meta, expenses[]}}} ─────────
function groupRows(rows) {
  const byEmp = {};
  rows.forEach(r => {
    const en = r.employee_name;
    if (!byEmp[en]) byEmp[en] = { name: en, email: r.employee_email, company: r.company, trips: {} };
    const tid = r.trip_id;
    if (!byEmp[en].trips[tid]) {
      byEmp[en].trips[tid] = {
        id: tid, title: r.trip_title, destination: r.destination,
        advance: parseFloat(r.advance_amount || 0),
        totalExpenses: parseFloat(r.total_expenses || 0),
        status: r.trip_status,
        expenses: [],
      };
    }
    byEmp[en].trips[tid].expenses.push({
      id:       r.expense_id,
      date:     String(r.expense_date || "").substring(0, 10),
      category: r.category,
      subcat:   r.food_subcategory,
      desc:     r.description,
      amount:   parseFloat(r.amount || 0),
      billType: r.bill_type,
    });
  });
  return byEmp;
}

// ── Category icons ───────────────────────────────────────────────────────────
const CAT_ICONS = { Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌" };

export default function ManagerExpenseReport() {
  const { user } = useAuth();
  const isEbekmed   = user?.company === "ebekmed";
  const companyName = isEbekmed ? "Ebek Med" : "Ebek";
  const BASE        = process.env.REACT_APP_API_URL || "https://ebek-expense-portal.onrender.com";
  const logoSrc     = isEbekmed ? `${BASE}/logo-ebekmed.png` : `${BASE}/logo-ebek.png`;
  const accentColor = isEbekmed ? "#0F6E56" : "#B71C1C";

  const [selYear,  setSelYear]  = useState(String(currentYear));
  const [selMonth, setSelMonth] = useState(String(currentMonth).padStart(2, "0"));
  const [loading,  setLoading]  = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Raw grouped data: { empName: { name, email, trips: {id: {…,expenses:[]}} } }
  const [grouped, setGrouped] = useState({});

  // Per-expense comments: { expenseId: string }
  const [comments, setComments] = useState({});

  // Per-expense reviewed: { expenseId: bool }
  const [reviewed, setReviewed] = useState({});

  // Manager overall note
  const [managerNote, setManagerNote] = useState("");

  // Submitted flag
  const [submitted, setSubmitted] = useState(false);

  // Ask-query modal state
  const [queryModal, setQueryModal] = useState(null); // { expenseId, expenseLabel }
  const [queryText,  setQueryText]  = useState("");
  const [queryBusy,  setQueryBusy]  = useState(false);

  // Inject print CSS once
  useEffect(() => {
    const tag = document.createElement("style");
    tag.textContent = PRINT_CSS;
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  // ── Fetch ────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setGrouped({});
    setComments({});
    setReviewed({});
    setSubmitted(false);
    try {
      const month = `${selYear}-${selMonth}`;
      const res = await API.get(`/expense-report/team-expenses?month=${month}`);
      setGrouped(groupRows(res.data));
    } catch {
      toast.error("Failed to load team expenses");
    } finally {
      setLoading(false);
    }
  }, [selYear, selMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Derived totals ───────────────────────────────────────────────────────
  const empList = useMemo(() => Object.values(grouped), [grouped]);

  const totals = useMemo(() => {
    let advance = 0, spent = 0;
    empList.forEach(emp => {
      Object.values(emp.trips).forEach(t => {
        advance += t.advance;
        spent   += t.expenses.reduce((s, e) => s + e.amount, 0);
      });
    });
    return { advance, spent, diff: spent - advance };
  }, [empList]);

  const allExpenseIds = useMemo(() => {
    const ids = [];
    empList.forEach(emp => Object.values(emp.trips).forEach(t => t.expenses.forEach(e => ids.push(e.id))));
    return ids;
  }, [empList]);

  const reviewedCount  = allExpenseIds.filter(id => reviewed[id]).length;
  const totalExpenses  = allExpenseIds.length;
  const allReviewed    = totalExpenses > 0 && reviewedCount === totalExpenses;

  // ── Helpers ──────────────────────────────────────────────────────────────
  const setComment  = (id, val) => setComments(p => ({ ...p, [id]: val }));
  const toggleReview= (id) => setReviewed(p => ({ ...p, [id]: !p[id] }));
  const markAllReviewed = () => {
    const next = {};
    allExpenseIds.forEach(id => next[id] = true);
    setReviewed(next);
  };

  // ── Ask Query ────────────────────────────────────────────────────────────
  const handleAskQuery = async () => {
    if (!queryText.trim() || !queryModal) return;
    setQueryBusy(true);
    try {
      await API.post(`/queries/expense/${queryModal.expenseId}`, { question: queryText.trim() });
      toast.success("Query sent! Employee will be notified.", { duration: 4000 });
      setQueryModal(null);
      setQueryText("");
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to send query");
    } finally {
      setQueryBusy(false);
    }
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    // Guard: prevent double-submit
    if (submitting || submitted) return;
    setShowPreview(false);   // close preview modal if open
    setSubmitting(true);
    try {
      const employees = empList.map(emp => {
        const trips = Object.values(emp.trips).map(t => ({
          title:      t.title,
          destination:t.destination,
          expenses:   t.expenses.map(e => ({
            id:       e.id,
            date:     e.date,
            category: e.category,
            amount:   e.amount,
            comment:  comments[e.id] || "",
            reviewed: reviewed[e.id] || false,
          })),
        }));
        const spent   = Object.values(emp.trips).reduce((s,t) => s + t.expenses.reduce((ss,e)=>ss+e.amount,0), 0);
        const advance = Object.values(emp.trips).reduce((s,t) => s + t.advance, 0);
        return { name: emp.name, email: emp.email, advance, spent, trips };
      });

      // Race the API call against a 45-second timeout so button never stays stuck
      const res = await Promise.race([
        API.post("/expense-report/submit", {
          month: selMonth, year: selYear, managerNote, employees,
        }),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("__timeout__")), 45000)
        ),
      ]);

      // Success path — update UI before toast so banner appears instantly
      setSubmitting(false);
      setSubmitted(true);
      toast.success(
        `📧 Report emailed to Neslyn and MD — ${res.data.message || "Sent successfully!"}`,
        { duration: 8000 }
      );
    } catch (err) {
      setSubmitting(false);
      const msg =
        err?.message === "__timeout__"
          ? "Request timed out — the email may still have been sent. Please check with Neslyn or MD."
          : err?.response?.data?.error || "Failed to submit report";
      toast.error(msg, { duration: 7000 });
    }
  };

  // ── Print/PDF ────────────────────────────────────────────────────────────
  const handlePrint = () => window.print();

  // ── Styles ───────────────────────────────────────────────────────────────
  const card = (extra = {}) => ({
    background: "var(--bg-card)", borderRadius: 14,
    border: "1px solid var(--border)", padding: "18px 20px", ...extra,
  });
  const selStyle = {
    padding: "8px 12px", borderRadius: 8,
    border: "1.5px solid #cbd5e1",
    background: "#ffffff", color: "#111827",
    fontSize: 13, fontFamily: "inherit", colorScheme: "light",
  };
  const monthLabel = `${MONTHS_FULL[parseInt(selMonth, 10) - 1]} ${selYear}`;

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{`
        .mer-dark-sel { background:#fff !important; color:#111827 !important; color-scheme:light; }
        .mer-dark-sel option { background:#fff !important; color:#111827 !important; }
        .mer-comment { background:#fff; border:1.5px solid #cbd5e1; border-radius:8px;
          color:#111827; font-family:inherit; font-size:12px; padding:7px 10px; resize:vertical;
          width:100%; min-height:52px; transition:border-color .2s; }
        .mer-comment::placeholder { color:#4b5563; opacity:1; }
        .mer-comment:focus { outline:none; border-color:rgba(129,140,248,.6); }
        .mer-check-row { display:flex; align-items:center; gap:6px; cursor:pointer; user-select:none; }
        .mer-check { width:17px; height:17px; accent-color:${accentColor}; cursor:pointer; }
        .mer-expense-row { border-top:1px solid #e5e7eb; padding:12px 0; color:#111827; }
        .mer-trip-card { background:#fff; border:1px solid #dbe3ec;
          border-radius:10px; overflow:hidden; margin-bottom:14px; }
        .mer-trip-header { padding:10px 14px; background:#f8fafc;
          display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:8px; }
        .mer-emp-card { background:#fff; border:1px solid #dbe3ec;
          border-radius:12px; overflow:hidden; margin-bottom:18px; }
        .mer-emp-header { padding:14px 18px; background:#f1f5f9;
          border-bottom:1px solid #dbe3ec; }
        @media(max-width:680px){
          .mer-exp-grid { grid-template-columns:1fr !important; }
        }
      `}</style>

      {/* ── Page header ────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title">📋 Submit Expense Report</h1>
        <p className="page-subtitle">Review team expenses, add comments, mark reviewed, then submit to Neslyn and MD</p>
      </div>

      {/* ── Filter bar ─────────────────────────────────────────────────── */}
      <div className="card mer-no-print" style={{ marginBottom: 20, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", marginBottom: 5 }}>Year</div>
          <select className="mer-dark-sel" value={selYear} onChange={e => setSelYear(e.target.value)} style={selStyle}>
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", marginBottom: 5 }}>Month</div>
          <select className="mer-dark-sel" value={selMonth} onChange={e => setSelMonth(e.target.value)} style={selStyle}>
            {MONTHS_FULL.map((m, i) => (
              <option key={i} value={String(i + 1).padStart(2, "0")}>{m}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1 }} />
        {totalExpenses > 0 && (
          <button onClick={handlePrint} className="mer-no-print"
            style={{ padding: "9px 16px", borderRadius: 8, border: "1.5px solid rgba(255,255,255,0.2)", background: "transparent", color: "#f9fafb", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            🖨️ Print / Save PDF
          </button>
        )}
      </div>

      {/* ── Loading ─────────────────────────────────────────────────────── */}
      {loading && (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
          <div style={{ color: "#9ca3af", fontSize: 14 }}>Loading team expenses...</div>
        </div>
      )}

      {/* ── No data ─────────────────────────────────────────────────────── */}
      {!loading && empList.length === 0 && (
        <div style={{ textAlign: "center", padding: "60px 20px" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ color: "#d1d5db", fontSize: 16, fontWeight: 700 }}>No expenses found</div>
          <div style={{ color: "#9ca3af", fontSize: 13, marginTop: 6 }}>No team expenses recorded for {monthLabel}</div>
        </div>
      )}

      {/* ── Report content ──────────────────────────────────────────────── */}
      {!loading && empList.length > 0 && (
        <div id="mer-printable">

          {/* ── Print header (visible only on print) ── */}
          <div style={{ display: "none" }} className="mer-print-header">
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12, paddingBottom: 12, borderBottom: "2px solid #e5e7eb" }}>
              <img src={logoSrc} alt={companyName} style={{ height: 44 }} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#1f2937" }}>{companyName} — Monthly Expense Report</div>
                <div style={{ fontSize: 13, color: "#6b7280" }}>{monthLabel} · Submitted by {user?.name}</div>
              </div>
            </div>
          </div>

          {/* ── Summary KPIs ── */}
          <div className="card" style={{ marginBottom: 20, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12 }}>
            {[
              { label: "Team Members", val: empList.length,                          color: "#818cf8" },
              { label: "Total Advance", val: `₹${fmtInr(totals.advance)}`,          color: "#fbbf24" },
              { label: "Total Spent",   val: `₹${fmtInr(totals.spent)}`,            color: totals.diff > 0 ? "#f87171" : "#34d399" },
              { label: totals.diff > 0 ? "Over Budget" : "Under Budget",
                val: `₹${fmtInr(Math.abs(totals.diff))}`,                           color: totals.diff > 0 ? "#f87171" : "#34d399" },
              { label: "Expenses Reviewed", val: `${reviewedCount} / ${totalExpenses}`, color: allReviewed ? "#34d399" : "#fbbf24" },
            ].map(k => (
              <div key={k.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 4 }}>{k.label}</div>
                <div style={{ fontSize: 17, fontWeight: 800, color: k.color, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{k.val}</div>
              </div>
            ))}
          </div>

          {/* ── Manager's overall note ── */}
          <div className="card mer-no-print" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#d1d5db", marginBottom: 8 }}>📝 Manager's Overall Note <span style={{ fontWeight: 400, color: "#9ca3af", fontSize: 12 }}>(optional — appears in the emailed report)</span></div>
            <textarea
              className="mer-comment"
              rows={3}
              placeholder="e.g. Overspend this month is due to last-minute exam bookings in Chennai. All reviewed with employees."
              value={managerNote}
              onChange={e => setManagerNote(e.target.value)}
            />
          </div>

          {/* ── Employee blocks ── */}
          {empList.map(emp => {
            const trips    = Object.values(emp.trips);
            const empSpent = trips.reduce((s, t) => s + t.expenses.reduce((ss, e) => ss + e.amount, 0), 0);
            const empAdv   = trips.reduce((s, t) => s + t.advance, 0);
            const empDiff  = empSpent - empAdv;
            const empReviewedCount = trips.reduce((s,t) => s + t.expenses.filter(e => reviewed[e.id]).length, 0);
            const empTotalExp = trips.reduce((s,t) => s + t.expenses.length, 0);

            return (
              <div key={emp.name} className="mer-emp-card">
                {/* Employee header */}
                <div className="mer-emp-header">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#111827", marginBottom: 4 }}>👤 {emp.name}</div>
                      <div style={{ fontSize: 12, color: "#374151" }}>
                        Advance: <span style={{ color: "#fbbf24", fontWeight: 700 }}>₹{fmtInr(empAdv)}</span>
                        &nbsp;·&nbsp; Spent: <span style={{ color: empDiff > 0 ? "#f87171" : "#34d399", fontWeight: 700 }}>₹{fmtInr(empSpent)}</span>
                        &nbsp;·&nbsp; {empDiff > 0
                          ? <span style={{ color: "#f87171", fontWeight: 700 }}>Over ₹{fmtInr(empDiff)}</span>
                          : <span style={{ color: "#34d399", fontWeight: 700 }}>Under ₹{fmtInr(Math.abs(empDiff))}</span>
                        }
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: empReviewedCount === empTotalExp ? "#34d399" : "#fbbf24", fontWeight: 700 }}>
                      {empReviewedCount === empTotalExp ? "✅" : "⏳"} {empReviewedCount}/{empTotalExp} reviewed
                    </div>
                  </div>
                </div>

                {/* Trips */}
                <div style={{ padding: "14px 18px" }}>
                  {trips.map(trip => (
                    <div key={trip.id} className="mer-trip-card">
                      <div className="mer-trip-header">
                        <div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>✈️ {trip.title}</span>
                          {trip.destination && <span style={{ fontSize: 12, color: "#4b5563", marginLeft: 8 }}>📍 {trip.destination}</span>}
                        </div>
                        <div style={{ display: "flex", gap: 12, fontSize: 12, flexWrap: "wrap" }}>
                          <span style={{ color: "#fbbf24" }}>Advance: ₹{fmtInr(trip.advance)}</span>
                          <span style={{ color: trip.expenses.reduce((s,e)=>s+e.amount,0) > trip.advance ? "#f87171" : "#34d399" }}>
                            Spent: ₹{fmtInr(trip.expenses.reduce((s,e)=>s+e.amount,0))}
                          </span>
                        </div>
                      </div>

                      {/* Expense rows */}
                      <div style={{ padding: "0 14px 10px" }}>
                        {trip.expenses.map(exp => (
                          <div key={exp.id} className="mer-expense-row">
                            <div style={{ display: "grid", gridTemplateColumns: "90px 110px 1fr 140px", gap: 12, alignItems: "start" }} className="mer-exp-grid">

                              {/* Date + category */}
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>{exp.date}</div>
                                <div style={{ fontSize: 11, color: "#4b5563", marginTop: 2 }}>{exp.billType === "cash_voucher" ? "💵 Cash" : "🧾 Bill"}</div>
                              </div>

                              {/* Category + amount */}
                              <div>
                                <div style={{ fontSize: 13 }}>{CAT_ICONS[exp.category] || "📌"} {exp.category}</div>
                                {exp.subcat && <div style={{ fontSize: 11, color: "#4b5563" }}>{exp.subcat}</div>}
                                <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4, color: exp.amount >= 5000 ? "#b91c1c" : "#111827" }}>
                                  ₹{fmtInr(exp.amount)}
                                </div>
                                {exp.amount >= 5000 && (
                                  <div style={{ fontSize: 10, color: "#f87171", marginTop: 2 }}>⚠️ High expense</div>
                                )}
                              </div>

                              {/* Description + comment box */}
                              <div>
                                {exp.desc && (
                                  <div style={{ fontSize: 11, color: "#374151", marginBottom: 5, fontStyle: "italic" }}>"{exp.desc}"</div>
                                )}
                                <textarea
                                  className="mer-comment mer-no-print"
                                  placeholder="Manager's comment — explain if high or over budget..."
                                  value={comments[exp.id] || ""}
                                  onChange={e => setComment(exp.id, e.target.value)}
                                />
                                {/* Print view: show comment text */}
                                <div style={{ display: "none", fontSize: 12, color: "#6b7280", fontStyle: "italic", padding: "4px 0" }} className="mer-print-comment">
                                  {comments[exp.id] || "—"}
                                </div>
                              </div>

                              {/* Reviewed checkbox + Ask Query */}
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                                <label className="mer-check-row mer-no-print">
                                  <input
                                    type="checkbox"
                                    className="mer-check"
                                    checked={!!reviewed[exp.id]}
                                    onChange={() => toggleReview(exp.id)}
                                  />
                                  <span style={{ fontSize: 12, color: reviewed[exp.id] ? "#34d399" : "#9ca3af", fontWeight: reviewed[exp.id] ? 700 : 400 }}>
                                    {reviewed[exp.id] ? "✅ Reviewed" : "Reviewed with employee"}
                                  </span>
                                </label>
                                {/* Ask query button */}
                                <button
                                  className="mer-no-print"
                                  onClick={() => {
                                    setQueryModal({
                                      expenseId: exp.id,
                                      expenseLabel: `${exp.category} · ₹${fmtInr(exp.amount)} · ${exp.date}`,
                                    });
                                    setQueryText("");
                                  }}
                                  style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(129,140,248,0.4)", background: "rgba(129,140,248,0.08)", color: "#818cf8", fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                                  ❓ Ask Query
                                </button>
                                {/* Print view */}
                                <div style={{ display: "none", fontSize: 12 }} className="mer-print-reviewed">
                                  {reviewed[exp.id]
                                    ? <span style={{ color: "#059669", fontWeight: 700 }}>✓ Reviewed</span>
                                    : <span style={{ color: "#D97706" }}>⏳ Pending</span>
                                  }
                                </div>
                              </div>

                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* ── Declaration ── */}
          <div className="card" style={{ marginBottom: 20, borderLeft: `3px solid ${accentColor}` }}>
            <div style={{ fontSize: 13, color: "#d1d5db", lineHeight: 1.7 }}>
              I, <strong style={{ color: "#f9fafb" }}>{user?.name}</strong>, confirm that I have reviewed all expense
              claims listed above with each team member, verified supporting documents, and provided justification
              for any amount exceeding the approved advance. All feedback and review statuses are accurately reflected.
            </div>
          </div>

          {/* ── Submit bar ── */}
          <div className="card mer-no-print" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div style={{ fontSize: 13, color: "#9ca3af" }}>
              {reviewedCount > 0
                ? <span style={{ color: "#34d399", fontWeight: 700 }}>✅ {reviewedCount}/{totalExpenses} expenses reviewed</span>
                : <span style={{ color: "#9ca3af" }}>📋 {totalExpenses} expense{totalExpenses !== 1 ? "s" : ""} — review checkboxes are optional</span>
              }
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", display: "flex", alignItems: "center", gap: 4 }}>
                📧 Will be sent to <span style={{ color: "#e2e8f0", fontWeight: 700 }}>Neslyn@ebkindia.com and MD@ebekindia.com</span>
              </div>
              <button
                onClick={() => setShowPreview(true)}
                disabled={submitting || submitted}
                style={{
                  padding: "10px 20px", borderRadius: 10,
                  border: `1.5px solid ${accentColor}`, background: "transparent",
                  color: accentColor, fontWeight: 700, fontSize: 14, cursor: "pointer",
                  transition: "all .2s",
                }}>
                👁️ Review Report
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || submitted}
                style={{
                  padding: "10px 24px", borderRadius: 10,
                  border: "none",
                  background: submitted ? "#059669" : accentColor,
                  color: "white", fontWeight: 800, fontSize: 14, cursor: submitted ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.7 : 1, transition: "all .2s",
                }}>
                {submitted ? "✅ Report Submitted!" : submitting ? "⏳ Submitting..." : "📤 Submit Report to Neslyn & MD"}
              </button>
            </div>
          </div>

          {/* Success banner */}
          {submitted && (
            <div style={{ marginTop: 16, padding: "16px 20px", background: "rgba(5,150,105,0.15)", border: "1px solid rgba(5,150,105,0.4)", borderRadius: 12, color: "#34d399", fontSize: 13, fontWeight: 600 }}>
              ✅ Report for <strong>{monthLabel}</strong> has been submitted and emailed to <strong>Neslyn and MD</strong>.
              A copy was sent to your email (<strong>{user?.email}</strong>).
            </div>
          )}

        </div>
      )}

      {/* ── Ask Query Modal ───────────────────────────────────────────────── */}
      {queryModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => { setQueryModal(null); setQueryText(""); }}>
          <div style={{ background: "#1e2235", borderRadius: 16, padding: 28, maxWidth: 520, width: "100%", border: "1px solid rgba(255,255,255,0.12)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#f9fafb", marginBottom: 4 }}>❓ Ask Query about Expense</div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 18 }}>{queryModal.expenseLabel}</div>
            <textarea
              className="mer-comment"
              autoFocus
              rows={4}
              placeholder="e.g. Please provide the hotel invoice for this entry. The amount seems higher than usual for this destination."
              value={queryText}
              onChange={e => setQueryText(e.target.value)}
              style={{ marginBottom: 16 }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => { setQueryModal(null); setQueryText(""); }}
                style={{ padding: "9px 18px", borderRadius: 8, border: "1.5px solid rgba(255,255,255,0.15)", background: "transparent", color: "#9ca3af", fontSize: 13, cursor: "pointer", fontWeight: 600 }}>
                Cancel
              </button>
              <button onClick={handleAskQuery} disabled={queryBusy || !queryText.trim()}
                style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: accentColor, color: "white", fontWeight: 700, fontSize: 13, cursor: queryText.trim() && !queryBusy ? "pointer" : "not-allowed", opacity: !queryText.trim() || queryBusy ? 0.6 : 1 }}>
                {queryBusy ? "Sending..." : "Send Query 📩"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Full-Page Preview ──────────────────────────────────────────────── */}
      {showPreview && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "#f3f4f6", overflowY: "auto",
          fontFamily: "Inter, Arial, sans-serif",
        }}>

          {/* ── Sticky action bar ── */}
          <div style={{
            position: "sticky", top: 0, zIndex: 10,
            background: "#1f2937",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            padding: "0 24px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            minHeight: 64, gap: 12, flexWrap: "wrap",
          }}>
            {/* Left: back + title */}
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <button
                onClick={() => setShowPreview(false)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 16px", borderRadius: 8,
                  border: "1.5px solid rgba(255,255,255,0.2)",
                  background: "transparent", color: "#d1d5db",
                  fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}>
                ← Back to Edit
              </button>
              <div>
                <div style={{ color: "#f9fafb", fontWeight: 700, fontSize: 15 }}>
                  👁️ Report Preview
                </div>
                <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 1 }}>
                  {monthLabel} · {empList.length} employee{empList.length !== 1 ? "s" : ""} · {totalExpenses} expense{totalExpenses !== 1 ? "s" : ""}
                </div>
              </div>
            </div>

            {/* Right: summary pills + submit */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <span style={{ padding: "4px 12px", borderRadius: 20, background: "rgba(251,191,36,0.15)", color: "#fbbf24", fontSize: 12, fontWeight: 700 }}>
                  Advance ₹{fmtInr(totals.advance)}
                </span>
                <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                  background: totals.diff > 0 ? "rgba(248,113,113,0.15)" : "rgba(52,211,153,0.15)",
                  color:      totals.diff > 0 ? "#f87171" : "#34d399",
                }}>
                  Spent ₹{fmtInr(totals.spent)}
                </span>
              </div>
              <button
                onClick={() => { setShowPreview(false); handleSubmit(); }}
                disabled={submitting || submitted}
                style={{
                  padding: "10px 24px", borderRadius: 10, border: "none",
                  background: submitted ? "#059669" : accentColor,
                  color: "white", fontWeight: 800, fontSize: 14,
                  cursor: submitting || submitted ? "not-allowed" : "pointer",
                  opacity: submitting ? 0.7 : 1,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}>
                {submitted ? "✅ Submitted!" : submitting ? "⏳ Submitting..." : "📤 Confirm & Submit to Neslyn & MD"}
              </button>
            </div>
          </div>

          {/* ── Email preview body ── */}
          <div style={{ maxWidth: 780, margin: "32px auto 48px", padding: "0 16px" }}>

            {/* "This is how the email will look" banner */}
            <div style={{
              marginBottom: 20, padding: "10px 16px",
              background: "rgba(129,140,248,0.12)", border: "1px solid rgba(129,140,248,0.25)",
              borderRadius: 10, fontSize: 13, color: "#a5b4fc",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span style={{ fontSize: 16 }}>📧</span>
              This is exactly how the email will appear in <strong>Neslyn and MD's</strong> inboxes.
              &nbsp;Go back to make any changes before submitting.
            </div>

            {/* Email card */}
            <div style={{ background: "white", borderRadius: 14, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.10)" }}>

              {/* Email header */}
              <div style={{ background: accentColor, padding: "28px 32px" }}>
                <img src={logoSrc} alt={companyName} style={{ height: 44, display: "block", marginBottom: 14, maxWidth: 200 }} />
                <div style={{ color: "white", fontSize: 20, fontWeight: 800, marginBottom: 4 }}>
                  Monthly Expense Report
                </div>
                <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 13 }}>
                  {monthLabel} &nbsp;·&nbsp; Submitted by {user?.name}
                  &nbsp;·&nbsp; {new Date().toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" })}
                </div>
              </div>

              {/* Summary KPI strip */}
              <div style={{ padding: "20px 32px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: managerNote ? 16 : 0 }}>
                  {[
                    ["Team Size",     String(empList.length),           "#1f2937"],
                    ["Total Advance", `₹${fmtInr(totals.advance)}`,    "#1f2937"],
                    ["Total Spent",   `₹${fmtInr(totals.spent)}`,      totals.diff > 0 ? "#DC2626" : "#059669"],
                    [totals.diff > 0 ? "Over Budget" : "Under Budget",
                      `₹${fmtInr(Math.abs(totals.diff))}`,             totals.diff > 0 ? "#DC2626" : "#059669"],
                  ].map(([label, val, color]) => (
                    <div key={label} style={{ background: "white", padding: "12px 10px", borderRadius: 8, border: "1px solid #e5e7eb", textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Manager note */}
                {managerNote && (
                  <div style={{
                    padding: "14px 16px", background: "white",
                    border: "1px solid #e5e7eb", borderLeft: `4px solid ${accentColor}`,
                    borderRadius: "0 8px 8px 0", fontSize: 13, color: "#374151", lineHeight: 1.7
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 6 }}>
                      📝 Manager's Overall Note
                    </div>
                    {managerNote}
                  </div>
                )}
              </div>

              {/* Employee expense details */}
              <div style={{ padding: "24px 32px" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 18 }}>
                  Employee Expense Details
                </div>

                {empList.map(emp => {
                  const trips    = Object.values(emp.trips);
                  const empSpent = trips.reduce((s,t) => s + t.expenses.reduce((ss,e)=>ss+e.amount,0), 0);
                  const empAdv   = trips.reduce((s,t) => s + t.advance, 0);
                  const empDiff  = empSpent - empAdv;
                  const empRevCount = trips.reduce((s,t) => s + t.expenses.filter(e => reviewed[e.id]).length, 0);
                  const empTotalExp = trips.reduce((s,t) => s + t.expenses.length, 0);

                  return (
                    <div key={emp.name} style={{ marginBottom: 24, border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
                      {/* Employee row header */}
                      <div style={{ padding: "14px 18px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", marginBottom: 3 }}>👤 {emp.name}</div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            Advance: <strong style={{ color: "#D97706" }}>₹{fmtInr(empAdv)}</strong>
                            &nbsp;·&nbsp; Spent: <strong style={{ color: empDiff > 0 ? "#DC2626" : "#059669" }}>₹{fmtInr(empSpent)}</strong>
                            &nbsp;·&nbsp;
                            {empDiff > 0
                              ? <span style={{ color: "#DC2626", fontWeight: 700 }}>Over ₹{fmtInr(empDiff)}</span>
                              : <span style={{ color: "#059669", fontWeight: 700 }}>Under ₹{fmtInr(Math.abs(empDiff))}</span>}
                          </div>
                        </div>
                        <span style={{
                          fontSize: 12, padding: "4px 12px", borderRadius: 20, fontWeight: 700,
                          background: empRevCount === empTotalExp ? "#D1FAE5" : "#FEF3C7",
                          color:      empRevCount === empTotalExp ? "#065F46" : "#92400E",
                        }}>
                          {empRevCount === empTotalExp ? "✅" : "⏳"} {empRevCount}/{empTotalExp} reviewed
                        </span>
                      </div>

                      {/* Trips */}
                      <div style={{ padding: "14px 18px" }}>
                        {trips.map(trip => (
                          <div key={trip.id} style={{ marginBottom: 16 }}>
                            <div style={{
                              fontSize: 13, fontWeight: 700, color: "#374151",
                              marginBottom: 10, padding: "8px 12px",
                              background: "#f9fafb", borderRadius: 6, borderLeft: `3px solid ${accentColor}`,
                              display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8
                            }}>
                              <span>✈️ {trip.title}{trip.destination ? ` · 📍 ${trip.destination}` : ""}</span>
                              <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
                                {trip.expenses.length} expense{trip.expenses.length !== 1 ? "s" : ""}
                                &nbsp;·&nbsp; ₹{fmtInr(trip.expenses.reduce((s,e)=>s+e.amount,0))}
                              </span>
                            </div>

                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                              <thead>
                                <tr style={{ background: "#f3f4f6" }}>
                                  {["Date","Category","Amount","Manager's Comment","Reviewed"].map(h => (
                                    <th key={h} style={{ padding: "7px 12px", textAlign: "left", color: "#6b7280", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {trip.expenses.map((exp, idx) => (
                                  <tr key={exp.id} style={{ borderTop: "1px solid #f3f4f6", background: idx % 2 === 0 ? "white" : "#fafafa" }}>
                                    <td style={{ padding: "9px 12px", color: "#374151", whiteSpace: "nowrap" }}>{exp.date}</td>
                                    <td style={{ padding: "9px 12px", color: "#374151" }}>
                                      {CAT_ICONS[exp.category] || "📌"} {exp.category}
                                      {exp.subcat && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>{exp.subcat}</div>}
                                    </td>
                                    <td style={{ padding: "9px 12px", fontWeight: 800, whiteSpace: "nowrap",
                                      color: exp.amount >= 5000 ? "#DC2626" : "#1f2937" }}>
                                      ₹{fmtInr(exp.amount)}
                                      {exp.amount >= 5000 && <div style={{ fontSize: 10, color: "#DC2626", fontWeight: 600 }}>⚠️ High</div>}
                                    </td>
                                    <td style={{ padding: "9px 12px", color: comments[exp.id] ? "#374151" : "#d1d5db", fontStyle: comments[exp.id] ? "normal" : "italic" }}>
                                      {comments[exp.id] || "No comment"}
                                    </td>
                                    <td style={{ padding: "9px 12px", textAlign: "center" }}>
                                      {reviewed[exp.id]
                                        ? <span style={{ background: "#D1FAE5", color: "#065F46", padding: "3px 10px", borderRadius: 20, fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>✓ Reviewed</span>
                                        : <span style={{ background: "#FEF3C7", color: "#92400E", padding: "3px 10px", borderRadius: 20, fontSize: 11, whiteSpace: "nowrap" }}>⏳ Pending</span>
                                      }
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              {/* Trip subtotal row */}
                              <tfoot>
                                <tr style={{ background: "#f9fafb", borderTop: "2px solid #e5e7eb" }}>
                                  <td colSpan={2} style={{ padding: "8px 12px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>
                                    Trip Total
                                  </td>
                                  <td style={{ padding: "8px 12px", fontWeight: 800, color: "#1f2937", fontSize: 13 }}>
                                    ₹{fmtInr(trip.expenses.reduce((s,e)=>s+e.amount,0))}
                                  </td>
                                  <td colSpan={2} />
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Declaration + signature */}
              <div style={{ padding: "20px 32px", background: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
                <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.8, margin: "0 0 18px" }}>
                  I, <strong>{user?.name}</strong>, confirm that I have reviewed all expense claims listed above with
                  each team member, verified supporting documents, and provided justification for any amount
                  exceeding the approved advance. All feedback and review statuses are accurately reflected.
                </p>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <div style={{ width: 160, borderTop: "1px solid #374151", marginBottom: 6 }} />
                    <div style={{ fontSize: 12, color: "#6b7280" }}>Manager's Signature</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1f2937", marginTop: 2 }}>{user?.name}</div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 11, color: "#9ca3af", lineHeight: 1.8 }}>
                    Generated by {companyName} Expense Portal<br />
                    {new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST
                  </div>
                </div>
              </div>

            </div>{/* end email card */}

            {/* Bottom action bar */}
            <div style={{
              marginTop: 24, padding: "18px 24px",
              background: "#1f2937", borderRadius: 12,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              flexWrap: "wrap", gap: 12,
            }}>
              <div style={{ fontSize: 13, color: "#9ca3af" }}>
                📧 Will be sent to{" "}
                <span style={{ color: "#e2e8f0", fontWeight: 700 }}>Neslyn@ebkindia.com and MD@ebekindia.com</span>
                {" "}and CC'd to{" "}
                <span style={{ color: "#818cf8", fontWeight: 700 }}>{user?.email}</span>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setShowPreview(false)}
                  style={{
                    padding: "10px 20px", borderRadius: 10,
                    border: "1.5px solid rgba(255,255,255,0.2)", background: "transparent",
                    color: "#d1d5db", fontWeight: 700, fontSize: 13, cursor: "pointer",
                  }}>
                  ← Back to Edit
                </button>
                <button
                  onClick={() => { setShowPreview(false); handleSubmit(); }}
                  disabled={submitting || submitted}
                  style={{
                    padding: "10px 28px", borderRadius: 10, border: "none",
                    background: submitted ? "#059669" : accentColor,
                    color: "white", fontWeight: 800, fontSize: 14,
                    cursor: submitting || submitted ? "not-allowed" : "pointer",
                    opacity: submitting ? 0.7 : 1,
                  }}>
                  {submitted ? "✅ Submitted!" : submitting ? "⏳ Submitting..." : "📤 Confirm & Submit to Neslyn & MD"}
                </button>
              </div>
            </div>

          </div>{/* end maxWidth wrapper */}
        </div>
      )}
    </div>
  );
}
